import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { ImageCarousel } from '../../components/site/ImageCarousel';
import { schemeImage } from '../../lib/site';
import { formatRent, bedsLabel, type Listing } from './ListingCard';

/**
 * The page around a form on a home — /rent/:slug/viewing (Viewing.tsx): the title, then the form on the left (seven of twelve columns) with the home's card beside it on a desktop (one line under
 * the title on phones and tablets); once sent, a centred thank-you instead. No "back" link: the browser's back button
 * does that. The page owns its form; `useListing` is the shared query for the home.
 */
export const useListing = (slug: string | undefined) =>
  useQuery({ queryKey: ['public-listing', slug], queryFn: async () => (await api.public.get<Listing>(`/public/listings/${slug}`)).data, enabled: !!slug });

export const rentLine = (l: Listing | undefined) => (l ? `${formatRent(l.advertisedRentMinor, l.advertisedRentCurrency)} per ${l.rentPeriod === 'YEAR' ? 'year' : 'month'}` : '');

const r = (i: number) => ({ '--i': i }) as CSSProperties;

export function ListingFormPage({ listing: l, title, aside, done, children }: {
  slug?: string | undefined; listing: Listing | undefined; title: ReactNode;
  /** Extra lines inside the home's card on desktop, under the rent (e.g. what the viewing involves). */
  aside?: ReactNode;
  /** Sent: the thank-you (ListingFormThanks) in place of the form. */
  done?: ReactNode;
  /** The form. */
  children: ReactNode;
}) {
  const rent = rentLine(l);
  return (
    <SiteLayout chat={false} footer={false}>
      <div className="site-narrow page-head pb-16 lg:pb-8">
        {done ?? (
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-x-12 xl:gap-x-16">
            <div className="min-w-0 lg:col-span-7">
              {title && <h1 className="rise h-section text-ink-900" style={r(0)}>{title}</h1>}
              {/* Phones and tablets: the home in one line under the title (the full card sits beside the form on desktop). */}
              {l && (
                <div className={`rise mb-8 flex items-center gap-4 rounded-[16px] border border-ink-200 bg-white p-3 lg:hidden ${title ? 'mt-8' : ''}`} style={r(2)}>
                  <img src={l.media?.[0] ?? schemeImage(l.property?.slug)} alt="" className="h-16 w-20 shrink-0 rounded-[12px] object-cover" />
                  <div className="min-w-0 leading-snug">
                    <p className="truncate text-[15px] font-medium text-ink-900">{l.headline}</p>
                    <p className="mt-0.5 truncate text-[13px] text-ink-500">{bedsLabel(l.unit?.bedrooms)} · {rent}</p>
                  </div>
                </div>
              )}
              <div className="rise" style={r(3)}>{children}</div>
            </div>

            <aside className="hidden lg:col-span-4 lg:col-start-9 lg:block">
              <div className="rise surface overflow-hidden lg:sticky lg:top-28" style={r(3)}>
                <ImageCarousel images={l?.media?.length ? l.media : [schemeImage(l?.property?.slug)]} className="lg:aspect-[16/10]" />
                <div className="p-6">
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-400">{l?.property?.name} · {l?.property?.city}</p>
                  <p className="h-card mt-1.5 text-ink-900">{l?.headline}</p>
                  {l && <p className="mt-2.5 text-[15px] text-ink-600">{bedsLabel(l.unit?.bedrooms)} · {rent}</p>}
                  {aside && <div className="mt-5 border-t border-ink-100 pt-5">{aside}</div>}
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}

/** A line in the home's card: a small icon and a few words. */
export function AsideFact({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="flex items-center gap-3 text-[14px] text-ink-600 [&+&]:mt-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-50 text-bronze-500 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      <span>{children}</span>
    </p>
  );
}

/** The thank-you once a form is sent: a tick, the title, a line or two, and the ways on. */
export function ListingFormThanks({ title, children, actions }: { title: ReactNode; children: ReactNode; actions: ReactNode }) {
  return (
    <div className="rise mx-auto mt-10 max-w-xl text-center sm:mt-16">
      <CheckCircle2 className="mx-auto h-12 w-12 text-bronze-500" />
      <h1 className="h-section mt-6 text-ink-900">{title}</h1>
      <p className="mt-4 text-[16px] leading-relaxed text-ink-600">{children}</p>
      <div className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:justify-center">{actions}</div>
    </div>
  );
}
